<script setup lang="ts">
import { h, computed, ref, onUnmounted, type Component } from 'vue'
import TooltipPortal from '@/components/common/TooltipPortal.vue'

interface Props {
  text?: string
  type?: 'button' | 'submit' | 'reset'
  variant?: string
  size?: string
  disabled?: boolean
  loading?: boolean           // NEW
  loadingText?: string        // NEW
  iconPosition?: 'left' | 'right'  // NEW
  customClasses?: string
  icon?: string | Component | object | null | undefined
  tooltip?: string
  tooltipPosition?: 'top' | 'right' | 'bottom' | 'left'
}

const props = withDefaults(defineProps<Props>(), {
  type: 'button',
  variant: 'primary',
  size: 'md',
  disabled: false,
  loading: false,
  iconPosition: 'left',
  tooltipPosition: 'bottom'
})

defineEmits(['click'])

const buttonRef = ref<HTMLElement>()
const showTooltip = ref(false)

const isIconOnly = computed(() => !props.text && !props.loadingText && !!props.icon)
const isDisabled = computed(() => props.disabled || props.loading)
const displayText = computed(() => props.loading && props.loadingText ? props.loadingText : props.text)

const handleMouseEnter = () => {
  if (props.tooltip && !isDisabled.value) showTooltip.value = true
}
const handleMouseLeave = () => { showTooltip.value = false }

// Spinner size per button size
const spinnerSizeClasses = computed(() => {
  switch (props.size) {
    case 'xss': return 'w-3 h-3 border'
    case 'xs':  return 'w-3.5 h-3.5 border'
    case 'sm':  return 'w-4 h-4 border-2'
    case 'lg':  return 'w-5 h-5 border-2'
    default:    return 'w-4 h-4 border-2'
  }
})

// Spinner color per variant — matches text color on that bg
const spinnerColorClasses = computed(() => {
  switch (props.variant) {
    case 'secondary':
      return 'border-gray-400/30 border-t-gray-600 dark:border-white/20 dark:border-t-white'
    case 'normal':
      return 'border-gray-400/30 border-t-gray-600 dark:border-white/20 dark:border-t-white'
    case 'danger':
    case 'dangernormal':
      return 'border-white/30 border-t-white'
    case 'success':
    case 'successnormal':
      return 'border-white/30 border-t-white'
    case 'primarynormal':
    case 'primaryoutline':
      return 'border-blue-300/30 border-t-blue-500'
    default:
      return 'border-white/30 border-t-white'
  }
})

const iconOnlySizeClasses = computed(() => {
  switch (props.size) {
    case 'xss': return 'w-7 h-7'
    case 'xs':  return 'w-9 h-9'
    case 'sm':  return 'w-10 h-10'
    case 'lg':  return 'w-14 h-14'
    default:    return 'w-11 h-11'
  }
})

const iconSizeClasses = computed(() => {
  switch (props.size) {
    case 'xss': return 'w-4 h-4'
    case 'xs':  return 'w-5 h-5'
    case 'sm':  return 'w-6 h-6'
    case 'lg':  return 'w-8 h-8'
    default:    return 'w-7 h-7'
  }
})

const renderIcon = computed(() => {
  if (!props.icon) return null
  const iconClasses = ['inline-block', iconSizeClasses.value]
  if (typeof props.icon === 'string') {
    return h('span', { class: iconClasses, innerHTML: props.icon })
  }
  return h(props.icon as Component, { class: iconClasses })
})

const variantClasses = computed(() => {
  switch (props.variant) {
    case 'forinput':
      return 'shadow-theme-xs text-gray-800 dark:text-white/90 bg-transparent dark:bg-gray-900 border border-gray-300 dark:border-gray-700 focus:border-brand-300 dark:focus:border-brand-800 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 disabled:border-gray-100 disabled:placeholder:text-gray-300 dark:disabled:border-gray-800 dark:disabled:placeholder:text-white/15 rounded-r-md'
    case 'none':         return 'bg-transparent border-none'
    case 'normal':       return 'bg-transparent hover:bg-gray-100 dark:hover:bg-white/10 border-gray-300 dark:border-white/10'
    case 'secondary':    return 'bg-gray-200 hover:bg-gray-300 dark:bg-white/5 dark:hover:bg-white/10 text-gray-700 hover:text-gray-800 dark:text-white/90 border-0'
    case 'danger':       return 'bg-red-500 text-white hover:bg-red-600 border-red-500 hover:border-red-600'
    case 'dangernormal': return 'bg-transparent hover:bg-red-600 hover:text-white border-gray-300 dark:border-white/10 hover:border-red-600'
    case 'success':      return 'bg-green-500 text-white hover:bg-green-600 border-green-500 hover:border-green-600'
    case 'successnormal':return 'bg-transparent hover:bg-green-600 hover:text-white border-gray-300 dark:border-white/10 hover:border-green-600'
    case 'primaryoutline':return 'bg-blue-500/10 hover:bg-blue-500/20 text-blue-500 hover:text-blue-600 border-blue-500 hover:border-blue-600'
    case 'primarynormal': return 'bg-transparent hover:bg-blue-600 hover:text-white border-gray-300 dark:border-white/10 hover:border-blue-600'
    default:             return 'bg-blue-500 text-white hover:bg-blue-600 border-blue-500 hover:border-blue-600'
  }
})

const sizeClasses = computed(() => {
  if (isIconOnly.value) return iconOnlySizeClasses.value
  switch (props.size) {
    case 'xss': return 'py-1 px-2 text-xs h-7'
    case 'xs':  return 'py-1 px-3 text-xs h-9'
    case 'sm':  return 'py-2 px-4 text-sm h-10'
    case 'lg':  return 'py-4 px-8 text-lg h-14'
    default:    return 'py-2.5 px-6 text-base h-11'
  }
})

const iconOnlyClasses = computed(() => 'flex items-center justify-center')

onUnmounted(() => { showTooltip.value = false })
</script>

<template>
  <button
    ref="buttonRef"
    :type="type"
    :disabled="isDisabled"
    :aria-label="tooltip || text"
    :aria-busy="loading"
    @click="!loading && $emit('click')"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @focus="handleMouseEnter"
    @blur="handleMouseLeave"
    :class="[
      'rounded-md font-light transition border whitespace-nowrap gap-2',
      sizeClasses,
      variantClasses,
      iconOnlyClasses,
      customClasses,
      { 'opacity-50 cursor-not-allowed': isDisabled },
    ]"
  >
    <!-- Spinner (left) or Icon (left) -->
    <span
      v-if="loading && iconPosition === 'left'"
      :class="['inline-block rounded-full animate-spin flex-shrink-0', spinnerSizeClasses, spinnerColorClasses]"
      aria-hidden="true"
    />
    <component
      v-else-if="icon && iconPosition === 'left'"
      :is="renderIcon"
    />

    <!-- Text -->
    <template v-if="displayText">{{ displayText }}</template>

    <!-- Spinner (right) or Icon (right) -->
    <span
      v-if="loading && iconPosition === 'right'"
      :class="['inline-block rounded-full animate-spin flex-shrink-0', spinnerSizeClasses, spinnerColorClasses]"
      aria-hidden="true"
    />
    <component
      v-else-if="icon && iconPosition === 'right'"
      :is="renderIcon"
    />
  </button>

  <TooltipPortal
    v-if="tooltip && !isDisabled"
    :target="buttonRef"
    :visible="showTooltip"
    :text="tooltip"
    :position="tooltipPosition"
  />
</template>
